import * as React from 'react'

import { inject } from 'mobx-react'
import MagicUrl from 'quill-magic-url'
import ReactDOM from 'react-dom'
import ReactQuill, { Quill } from 'react-quill-new'

import {
  IAttachment,
  IFileRequisites,
  IUploadLog,
  UploadingType,
} from '~/client/graph'
import {
  FilePicker,
  IFilePickerActions,
} from '~/client/src/shared/components/FilePicker'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import FileType from '../../../enums/FileType'
import IFilePreviewProperties from '../../../interfaces/IFilePreviewProperties'
import { FileUploadingStore } from '../../../stores/domain/FileUploading.store'
import { SHOW_FULLSCREEN_PREVIEW } from '../../../stores/EventStore/eventConstants'
import EventsStore from '../../../stores/EventStore/Events.store'
import IAttachmentAdditionalInfo from '../../../types/IAttachmentAdditionalInfo'
import formatBytes from '../../../utils/formatBytes'
import QuillVideoParser from '../../../utils/QuillVideoParser'
import VideoPlayer from '../../VideoPlayer/VideoPlayer'

import 'react-quill-new/dist/quill.snow.css'
import Colors from '~/client/src/shared/theme.module.scss'

const QuillEmbed = Quill.import('blots/block/embed') as any
const QuillHeader = Quill.import('formats/header') as any
const QuillImage = Quill.import('formats/image') as any
const QuillVideo = Quill.import('formats/video') as any

const imageRegex = /\.(gif|jpe?g|tiff?|png|webp|bmp|jfif)$/i

const DIVIDER_BLOT_NAME = 'hr'
export const VIDEO_BLOT_NAME = 'video'
export const IMAGE_GALLERY_BLOT_NAME = 'gallery'

interface IProps {
  id: string

  content: string
  isReadOnly?: boolean
  fileUploadingStore?: FileUploadingStore
  eventsStore?: EventsStore

  applyContent(content: string): void
  addAttachment(attachment: IAttachment): void
  additionalInfo?: IAttachmentAdditionalInfo
  addToUpdateLogList?: (log: IUploadLog) => void
}

@inject('eventsStore', 'fileUploadingStore')
export default class TextEditor extends React.Component<IProps> {
  private modules = {
    toolbar: {
      container: `#${this.props.id}`,
      handlers: {
        image: this.imageHandler.bind(this),
        hr: this.hrHandler.bind(this),
        'custom-header': this.customHeaderHandler.bind(this),
      },
    },
    magicUrl: true,
    quillVideoParser: true,
    clipboard: {
      matchVisual: false,
    },
  }

  private formats = [
    'image',
    'link',
    'bold',
    'underline',
    'list',
    DIVIDER_BLOT_NAME,
    'header',
    IMAGE_GALLERY_BLOT_NAME,
    'video',
  ]
  private quill
  private readonly imagePicker = React.createRef<IFilePickerActions>()

  public constructor(props: IProps) {
    super(props)

    Quill.register({
      [`formats/${DIVIDER_BLOT_NAME}`]: Hr,
      'formats/header': Header,
      'formats/image': Image,
      [`formats/${IMAGE_GALLERY_BLOT_NAME}`]: ImageGallery,
      'formats/video': VideoPreview,
      'modules/magicUrl': MagicUrl,
      'modules/quillVideoParser': QuillVideoParser,
    })
  }

  public render() {
    const { content, isReadOnly } = this.props
    return (
      <>
        <ReactQuill
          value={content}
          onChange={this.onContentChange}
          placeholder={Localization.translator.addTextHere}
          modules={this.modules}
          formats={this.formats}
          bounds=".text-editor"
          ref={this.setQuillRef}
          readOnly={isReadOnly}
          className="rich-text-editor"
        />
        <FilePicker
          actionsRef={this.imagePicker}
          accept="image/*"
          onPick={([file]) => this.insertImage(file)}
        />
      </>
    )
  }

  private onContentChange = (content: string, _delta: any, source: string) => {
    if (source === 'user') {
      this.props.applyContent(content)
    }
  }

  private setQuillRef = (ref: ReactQuill) => {
    if (ref) {
      this.quill = ref.getEditor()
      this.quill.root.addEventListener('drop', this.handleDrop, false)
      this.quill.root.addEventListener('click', this.handleClick)
    }
  }

  private handleDrop = e => {
    if (e.dataTransfer?.files?.length) {
      e.preventDefault()
      const images = []

      // Set quill cursor to the pointer position.
      if (document.caretRangeFromPoint) {
        const selection = document.getSelection()
        const range = document.caretRangeFromPoint(e.clientX, e.clientY)
        if (selection && range) {
          selection.setBaseAndExtent(
            range.startContainer,
            range.startOffset,
            range.startContainer,
            range.startOffset,
          )
        }
      }

      Array.from(e.dataTransfer.files).forEach(async (file: File) => {
        if (file.type === 'application/pdf') {
          const result = await this.uploadFile(
            file as File,
            UploadingType.Pdf,
            file.name,
          )

          this.props.addAttachment({
            fileName: file.name,
            size: formatBytes(file.size),
            url: result.fileURL,
          })
          return
        }

        if (imageRegex.test(file.name)) {
          images.push(file)
        }
      })

      if (images.length === 1) {
        this.insertImage(images[0])
      } else if (images.length > 1) {
        this.insertImageGallery(images)
      }
    }
  }

  private handleClick = e => {
    if (e.target?.tagName?.toLowerCase() === 'img') {
      e.stopPropagation()
      const images = this.getAllImagesInEditor()
      const index = images.findIndex(
        im => im.fileUrl === e.target.getAttribute('src'),
      )

      this.props.eventsStore.dispatch(SHOW_FULLSCREEN_PREVIEW, images, index)
    }
  }

  private imageHandler() {
    this.imagePicker.current?.open()
  }

  private async insertImage(file: File) {
    const result = await this.uploadFile(file, UploadingType.Image, file.name)

    const range = this.quill.getSelection(true)
    this.quill.insertEmbed(range.index, 'image', {
      src: result.fileURL,
      alt: file.name,
    })
    this.quill.insertText(range.index + 1, '\n')
    this.quill.setSelection(range.index + 2, range.length)

    this.props.applyContent(this.quill.root.innerHTML)
  }

  private async insertImageGallery(files: File[]) {
    const images = []
    for (const file of files) {
      const result = await this.uploadFile(file, UploadingType.Image, file.name)
      images.push({ url: result.fileURL, name: file.name })
    }

    const range = this.quill.getSelection(true)
    this.quill.insertEmbed(range.index, IMAGE_GALLERY_BLOT_NAME, images)
    this.quill.insertText(range.index + 1, '\n')
    this.quill.setSelection(range.index + 2, range.length)

    this.props.applyContent(this.quill.root.innerHTML)
  }

  private hrHandler() {
    const range = this.quill.getSelection()
    if (range) {
      this.quill.insertEmbed(range.index, DIVIDER_BLOT_NAME, 'null')
      this.quill.setSelection(range.index + 2, range.length)

      this.props.applyContent(this.quill.root.innerHTML)
    }
  }

  private getAllImagesInEditor = (): IFilePreviewProperties[] => {
    const imageElements = this.quill.root.getElementsByTagName('img')
    return Array.from(imageElements).map(image => ({
      fileUrl: (image as HTMLImageElement).getAttribute('src'),
      fileName: (image as HTMLImageElement).getAttribute('alt'),
      fileType: FileType.Image,
    }))
  }

  private async uploadFile(
    file: File,
    uploadingType: UploadingType,
    fileName: string,
  ): Promise<IFileRequisites> {
    const [result] = await this.props.fileUploadingStore.uploadFile(
      file,
      uploadingType,
      this.props.eventsStore.appState.activeProject.id,
      fileName,
      false,
      this.props.additionalInfo?.sourceEntityId,
      this.props.additionalInfo?.sourceEntityType,
      this.props.additionalInfo?.locationIds,
      this.props.additionalInfo?.sourceEntityCode,
      this.props.additionalInfo?.sourceEntityTypeName,
      this.props.additionalInfo?.locationName,
    )

    if (!result) {
      return
    }

    this.props.addToUpdateLogList?.(result.uploadLog)

    return result
  }

  private customHeaderHandler(value: string) {
    const { header } = this.quill.getFormat()

    const isSelected = `${header}` === `${value}`
    const newValue = isSelected ? undefined : value
    this.quill.format('header', newValue)
  }
}
class Hr extends QuillEmbed {
  public static blotName = DIVIDER_BLOT_NAME
  public static className = 'announcement-divider'
  public static tagName = 'hr'

  public static create() {
    const node = super.create()

    node.setAttribute(
      'style',
      'height: 0px; margin: 10px auto; width: 200px; line-height: 0;',
    )
    return node
  }
}

class ImageGallery extends QuillEmbed {
  public static blotName = IMAGE_GALLERY_BLOT_NAME
  public static className = IMAGE_GALLERY_BLOT_NAME
  public static tagName = 'div'

  public static create(images: any) {
    if (Array.isArray(images)) {
      const node = super.create()

      images.forEach(image => {
        const img = document.createElement('img')
        img.setAttribute('src', image.url)
        img.setAttribute('alt', image.name)
        img.setAttribute(
          'style',
          'padding: 4px; border-radius: 4px; max-height: 230px; cursor: pointer;',
        )
        node.appendChild(img)
      })

      node.setAttribute('style', 'margin: 22px 0;')
      node.contentEditable = false

      return node
    }

    // On redo images is the value from static value(domNode), that is already a created element.
    return images
  }

  // Needed to enable correct redo functionality.
  public static value(domNode: any) {
    return domNode
  }
}

class Image extends QuillImage {
  public static create(image: any) {
    const node = super.create(image.src)

    node.setAttribute('alt', image.alt)
    node.setAttribute(
      'style',
      'max-height: 385px; margin-top: 23px; margin-bottom: 23px; ' +
        'object-fit: contain; border-radius: 2px; cursor: pointer;',
    )

    return node
  }

  public static value(domNode: any) {
    return domNode
  }
}

class Header extends QuillHeader {
  public static create(headerValue: any) {
    const node = super.create(headerValue)

    if (headerValue === '0') {
      node.removeAttribute('style')
      return node
    }

    const headerToFontSizeMap = {
      '1': {
        fontSize: 32,
        marginTop: 35,
        marginBottom: 35,
      },
      '2': {
        fontSize: 24,
        marginTop: 35,
        marginBottom: 23,
      },
      '3': {
        fontSize: 20,
        marginTop: 23,
        marginBottom: 12,
      },
    }
    const headerStyle = headerToFontSizeMap[headerValue]

    node.setAttribute(
      'style',
      `color: ${Colors.error40}; ` +
        `font-size: ${headerStyle.fontSize}px; ` +
        `margin-top: ${headerStyle.marginTop}px; ` +
        `margin-bottom: ${headerStyle.marginBottom}px;`,
    )

    return node
  }
}

class VideoPreview extends QuillVideo {
  public static blotName = VIDEO_BLOT_NAME
  public static className = VIDEO_BLOT_NAME
  public static tagName = 'div'

  public static create(src) {
    const node = super.create()

    if (typeof src === 'object') {
      src = src.dataset.videoSrc
    }

    node.dataset.videoSrc = src
    ReactDOM.render(<VideoPlayer src={src} />, node)
    return node
  }

  public static value(domNode: any) {
    return domNode
  }
}
